
import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, AlertCircle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { formatBytes } from '@/utils/formatting';
import { Spinner } from '@/components/ui/Spinner';

interface EnhancedFileUploadProps {
  onFileUploaded?: (analysisId: string) => void;
}

const CSV_UPLOADS_MAX_SIZE = 50 * 1024 * 1024; // 50MB
const RAW_DATA_FILES_MAX_SIZE = 100 * 1024 * 1024; // 100MB

export const EnhancedFileUpload: React.FC<EnhancedFileUploadProps> = ({ onFileUploaded }) => {
  const queryClient = useQueryClient();
  const [selectedBucket, setSelectedBucket] = useState<string>('csv_uploads');
  const [uploading, setUploading] = useState(false);

  const bucketOptions = [
    { value: 'csv_uploads', label: 'CSV Uploads (Small Files)', maxSize: '50MB' },
    { value: 'raw-data-files', label: 'Raw Data Files (Large Files)', maxSize: '100MB' }
  ];

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // File type validation
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast({
        title: "Invalid file type",
        description: "Please upload a CSV file",
        variant: "destructive",
      });
      return;
    }

    // File size validation
    const maxSize = selectedBucket === 'csv_uploads' ? CSV_UPLOADS_MAX_SIZE : RAW_DATA_FILES_MAX_SIZE;
    if (file.size > maxSize) {
      toast({
        title: "File too large",
        description: `File size (${formatBytes(file.size)}) exceeds the ${formatBytes(maxSize)} limit for ${selectedBucket}. ${selectedBucket === 'csv_uploads' ? 'Try using the Raw Data Files bucket for larger files.' : 'Please reduce file size or split into smaller files.'}`,
        variant: "destructive",
      });
      return;
    }

    setUploading(true);

    try {
      // Upload to selected bucket
      const fileExt = file.name.split('.').pop();
      const filePath = `${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from(selectedBucket)
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Create analysis record
      const { data: analysisRecord, error: dbError } = await supabase
        .from('sys_util_csv_analysis')
        .insert({
          file_name: file.name,
          file_path: filePath,
          content_type: file.type,
          size: file.size,
        })
        .select()
        .single();

      if (dbError) throw dbError;

      console.log('Created analysis record:', analysisRecord);

      // Trigger analysis
      const { error: analysisError } = await supabase.functions
        .invoke('analyze-csv', {
          body: { 
            fileId: filePath,
            analysisId: analysisRecord.id,
            bucket: selectedBucket
          },
        });

      if (analysisError) {
        console.error('Analysis error:', analysisError);
        throw analysisError;
      }

      toast({
        title: "Success",
        description: `File uploaded to ${selectedBucket} successfully. Analyzing...`,
      });

      // Invalidate and refetch the csv analyses query
      await queryClient.invalidateQueries({ queryKey: ['csvAnalyses'] });
      
      if (onFileUploaded) {
        onFileUploaded(analysisRecord.id);
      }

    } catch (error: any) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  }, [queryClient, selectedBucket, onFileUploaded]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
    disabled: uploading,
  });

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
          <Upload className="w-5 h-5" />
          Upload New File
        </h3>
      </div>
      
      <div>
        <label className="text-sm font-medium mb-2 block">Target Bucket</label>
        <Select value={selectedBucket} onValueChange={setSelectedBucket} disabled={uploading}>
          <SelectTrigger>
            <SelectValue placeholder="Select bucket" />
          </SelectTrigger>
          <SelectContent>
            {bucketOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label} - Max: {option.maxSize}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          <strong>File Size Limits:</strong><br />
          • CSV Uploads: Up to {formatBytes(CSV_UPLOADS_MAX_SIZE)} (recommended for quick processing)<br />
          • Raw Data Files: Up to {formatBytes(RAW_DATA_FILES_MAX_SIZE)} (for larger datasets)
        </AlertDescription>
      </Alert>

      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${isDragActive ? 'border-primary bg-primary/10' : 'border-gray-300 hover:border-primary'}
          ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input {...getInputProps()} />
        {uploading ? (
          <div className="space-y-2">
            <Spinner size="lg" ariaLabel="Uploading and analyzing" />
            <p className="text-sm text-gray-600">Uploading and analyzing...</p>
          </div>
        ) : (
          <>
            <Upload className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">
              {isDragActive ? (
                "Drop the CSV file here..."
              ) : (
                `Drag and drop a CSV file here, or click to select (Max: ${formatBytes(selectedBucket === 'csv_uploads' ? CSV_UPLOADS_MAX_SIZE : RAW_DATA_FILES_MAX_SIZE)})`
              )}
            </p>
            <Button variant="outline" className="mt-4" disabled={uploading}>
              Select File
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
